// DPAAgreement.js
import React, { useState, useEffect } from 'react';
import { CheckIcon, DocumentTextIcon, UserIcon, ArrowDownTrayIcon, ClockIcon } from '@heroicons/react/20/solid';

const DPAAgreement = ({ onAccept, user, dpaStatus }) => {
  const [accepted, setAccepted] = useState(false);
  const [printName, setPrintName] = useState('');
  const [isAccepted, setIsAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState(null);
  const [acceptedBy, setAcceptedBy] = useState(null);
  const [acceptedPrintName, setAcceptedPrintName] = useState(null);

  // Set initial state from dpaStatus prop
  useEffect(() => {
    if (dpaStatus) {
      setIsAccepted(dpaStatus.accepted || false);
      setAcceptedAt(dpaStatus.acceptedAt);
      setAcceptedBy(dpaStatus.acceptedBy);
      setAcceptedPrintName(dpaStatus.printName);
      
      // If already accepted, pre-fill the print name
      if (dpaStatus.accepted && dpaStatus.printName) {
        setPrintName(dpaStatus.printName);
        setAccepted(true);
      }
    }
  }, [dpaStatus]);

  const handleAccept = () => {
    if (accepted && printName.trim()) {
      if (onAccept) {
        onAccept(printName.trim());
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Generate DPA PDF for download
  const handleDownloadDPA = () => {
    // This would generate a PDF of the DPA agreement
    // For now, we'll create a text version
    const dpaContent = `
DATA PROCESSING AGREEMENT (DPA)

Accepted by: ${acceptedPrintName || printName || 'N/A'}
Accepted on: ${formatDate(acceptedAt)}
Accepted by (email): ${acceptedBy || user?.email || 'N/A'}
Company: ${user?.companyCode || 'N/A'}

--- DPA TERMS ---

1. Scope and Role Definitions
• Roles: Controller acts as the data exporter. Insightos acts as the data importer.
• Compliance: Both parties will comply with all applicable global data protection laws (including GDPR).
• Instructions: Insightos will only process personal data based on written instructions from the Controller.

2. Technical and Organizational Security Measures
• Infrastructure & OS: Data is hosted using IONOS Cloud enterprise infrastructure on secure Ubuntu Linux servers.
• Server Location: All application servers and underlying databases are strictly located and hosted within the European Union (EU).
• Management Platform: Server configuration, security updates, and access controls are managed via the Plesk platform.
• Encryption Baseline: All personal, financial, and sensitive medical data is strictly encrypted at rest using industrial-grade AES-256 encryption standards.
• Authentication Governance: Multi-Factor Authentication (MFA) is enforced as standard for all administrative access.
• Security Frameworks: Insightos is fully compliant with the ISO 27001 security framework and is actively working towards formal accreditation.

3. Sub-processors
• Approved Directory: The current approved sub-processors are:
  1. IONOS Cloud (Infrastructure and EU-based data hosting)
  2. Plesk (Server administration and security maintenance)
  3. Stripe (Subscription payment processing and billing portal)
  4. Google LLC (Google Meet video conferencing and communications)
  5. Bunny Way d.o.o. / bunny.net (CDN and secure media/profile image storage)
• Notification of Changes: Insightos will notify the Controller of any changes to sub-processors at least 14 days in advance.

4. Data Subject Rights & Incident Notification
• Assistance: Insightos will provide reasonable assistance to help the Controller respond to data subjects exercising their legal privacy rights.
• Breach Notification: Insightos will notify the Controller without undue delay, and no later than 48 hours, after becoming aware of any accidental or unlawful personal data breach.
• Data Protection Contact: Insightos has designated an official Data Protection Officer. The DPO can be reached directly at ashleycook@insightos.co.uk.

5. Term and Data Deletion
• Deletion Timeline: All company profiles, HR files, financial logs, and medical records will be permanently and irreversibly purged from Insightos servers and backups within 30 days of contract termination.

SCHEDULE A: DETAILS OF PROCESSING
• Categories of Data Subjects: Controller's employees, contractors, and corporate end-users.
• Types of Personal Data: Corporate profiles, HR records, employee financial details, video/audio metadata, and user profile pictures.
• Special Categories of Data (Sensitive Data): Employee medical histories, health metrics, and occupational health data.
• Nature of Processing: Storage, hosting, video consultation hosting, image delivery optimization, and technical processing necessary to deliver the Insightos B2B SaaS platform services.

---
This DPA was accepted electronically on ${formatDate(acceptedAt)}
`;

    // Create a blob and download
    const blob = new Blob([dpaContent], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `DPA_Agreement_${user?.companyCode || 'Company'}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // If DPA is already accepted, show the status view
  if (isAccepted) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 mb-4">
            <CheckIcon className="h-8 w-8 text-emerald-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">DPA Already Accepted</h3>
          <p className="mt-2 text-sm text-gray-600 max-w-2xl mx-auto">
            Your Data Processing Agreement has been accepted. Below are the details of your acceptance.
          </p>
        </div>

        {/* Acceptance Details Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
              Acceptance Details
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Status</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  <CheckIcon className="h-3 w-3 mr-1" />
                  Accepted
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Accepted By</span>
                <span className="text-sm font-medium text-gray-900">{acceptedPrintName || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Email</span>
                <span className="text-sm font-medium text-gray-900">{acceptedBy || user?.email || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Accepted On</span>
                <span className="text-sm font-medium text-gray-900">{formatDate(acceptedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Download Button */}
        <div className="flex justify-end">
          <button
            onClick={handleDownloadDPA}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Download DPA Agreement
          </button>
        </div>

        {/* Compact DPA Summary for reference */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
              <span>View DPA Summary</span>
              <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="mt-4 text-xs text-gray-600 space-y-2">
              <p><strong>Data Processing Agreement</strong> - Accepted on {formatDate(acceptedAt)}</p>
              <p className="text-gray-500">For full details, please download the agreement using the button above.</p>
            </div>
          </details>
        </div>
      </div>
    );
  }

  // If not accepted, show the acceptance form
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 mb-4">
          <DocumentTextIcon className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900">Data Processing Agreement (DPA)</h3>
        <p className="mt-2 text-sm text-gray-600 max-w-2xl mx-auto">
          Please review and accept our Data Processing Agreement to continue with the onboarding process.
          This agreement ensures compliance with data protection laws and outlines how your data will be processed.
        </p>
      </div>

      {/* DPA Summary */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-6 max-h-96 overflow-y-auto prose prose-sm">
          <h4 className="font-bold text-gray-900 mb-4">DATA PROCESSING AGREEMENT (DPA)</h4>
          
          <div className="space-y-4 text-gray-700">
            <div>
              <h5 className="font-semibold text-gray-900">1. Scope and Role Definitions</h5>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li><strong>Roles:</strong> Controller acts as the data exporter. Insightos acts as the data importer.</li>
                <li><strong>Compliance:</strong> Both parties will comply with all applicable global data protection laws (including GDPR).</li>
                <li><strong>Instructions:</strong> Insightos will only process personal data based on written instructions from the Controller.</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-gray-900">2. Technical and Organizational Security Measures</h5>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li><strong>Infrastructure & OS:</strong> Data is hosted using IONOS Cloud enterprise infrastructure on secure Ubuntu Linux servers.</li>
                <li><strong>Server Location:</strong> All application servers and underlying databases are strictly located and hosted within the European Union (EU).</li>
                <li><strong>Management Platform:</strong> Server configuration, security updates, and access controls are managed via the Plesk platform.</li>
                <li><strong>Encryption Baseline:</strong> All personal, financial, and sensitive medical data is strictly encrypted at rest using industrial-grade AES-256 encryption standards.</li>
                <li><strong>Authentication Governance:</strong> Multi-Factor Authentication (MFA) is enforced as standard for all administrative access.</li>
                <li><strong>Security Frameworks:</strong> Insightos is fully compliant with the ISO 27001 security framework and is actively working towards formal accreditation.</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-gray-900">3. Sub-processors</h5>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li><strong>Approved Directory:</strong> The current approved sub-processors are:</li>
                <ul className="list-circle pl-5 text-sm">
                  <li>IONOS Cloud (Infrastructure and EU-based data hosting)</li>
                  <li>Plesk (Server administration and security maintenance)</li>
                  <li>Stripe (Subscription payment processing and billing portal)</li>
                  <li>Google LLC (Google Meet video conferencing and communications)</li>
                  <li>Bunny Way d.o.o. / bunny.net (CDN and secure media/profile image storage)</li>
                </ul>
                <li><strong>Notification of Changes:</strong> Insightos will notify the Controller of any changes to sub-processors at least 14 days in advance.</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-gray-900">4. Data Subject Rights & Incident Notification</h5>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li><strong>Assistance:</strong> Insightos will provide reasonable assistance to help the Controller respond to data subjects exercising their legal privacy rights.</li>
                <li><strong>Breach Notification:</strong> Insightos will notify the Controller without undue delay, and no later than 48 hours, after becoming aware of any accidental or unlawful personal data breach.</li>
                <li><strong>Data Protection Contact:</strong> Insightos has designated an official Data Protection Officer. The DPO can be reached directly at ashleycook@insightos.co.uk.</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-gray-900">5. Term and Data Deletion</h5>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li><strong>Deletion Timeline:</strong> All company profiles, HR files, financial logs, and medical records will be permanently and irreversibly purged from Insightos servers and backups within 30 days of contract termination.</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-gray-900">SCHEDULE A: DETAILS OF PROCESSING</h5>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li><strong>Categories of Data Subjects:</strong> Controller's employees, contractors, and corporate end-users.</li>
                <li><strong>Types of Personal Data:</strong> Corporate profiles, HR records, employee financial details, video/audio metadata, and user profile pictures.</li>
                <li><strong>Special Categories of Data (Sensitive Data):</strong> Employee medical histories, health metrics, and occupational health data.</li>
                <li><strong>Nature of Processing:</strong> Storage, hosting, video consultation hosting, image delivery optimization, and technical processing necessary to deliver the Insightos B2B SaaS platform services.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Print Name Input */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="space-y-2">
          <label htmlFor="printName" className="flex items-center text-sm font-medium text-gray-700">
            <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
            Print Full Name
          </label>
          <input
            type="text"
            id="printName"
            value={printName}
            onChange={(e) => setPrintName(e.target.value)}
            placeholder="Enter your full legal name as it appears on official documents"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            By printing your name, you acknowledge that you are authorized to enter into this agreement on behalf of your organization.
          </p>
        </div>
      </div>

      {/* Acceptance Checkbox */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              id="dpa-accept"
              type="checkbox"
              checked={accepted}
              onChange={() => setAccepted(!accepted)}
              className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="dpa-accept" className="text-sm font-medium text-gray-900 cursor-pointer">
              I have read and agree to the Data Processing Agreement
            </label>
            <p className="text-xs text-gray-600 mt-1">
              By accepting this agreement, you confirm that you have the authority to enter into this agreement on behalf of your organization.
            </p>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <button
          onClick={handleAccept}
          disabled={!accepted || !printName.trim()}
          className={classNames(
            "inline-flex items-center px-6 py-3 rounded-xl font-semibold text-white transition-all duration-200",
            accepted && printName.trim()
              ? "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl cursor-pointer active:scale-95"
              : "bg-gray-300 cursor-not-allowed"
          )}
        >
          <CheckIcon className="h-5 w-5 mr-2" />
          Accept and Continue
        </button>
      </div>
    </div>
  );
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default DPAAgreement;